import { yupResolver } from "@hookform/resolvers/yup";
import { sendPasswordResetEmail } from "firebase/auth";
import * as React from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { FirebaseError } from "firebase/app";
import * as Sentry from "@sentry/react";
import { Button, Input } from "@jhool-io/fe-components";
import useToast from "../../../hooks/useToast";
import AuthWrapper from "../components/AuthWrapper/AuthWrapper";
import { auth } from "../../../firebase";
import { useEncryptAuthFailureEmail } from "../hooks/auth.mutations";

interface IResetPasswordProps {
    email: string;
}

const schema = yup.object({
    email: yup
        .string()
        .email("Please enter a valid email")
        .required("Email is required"),
});
export default function ResetPasswordPage() {
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<IResetPasswordProps>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const { toast } = useToast();
    const { mutateAsync: encryptEmail } = useEncryptAuthFailureEmail();

    const getEncryptedEmailForReset = async (
        email: string,
        reason: string
    ): Promise<string | null> => {
        try {
            const response = await encryptEmail({
                email,
                failure_type: "reset_password",
                metadata: {
                    pp_reason: reason,
                },
            });
            return response.encrypted_email;
        } catch (encryptError) {
            Sentry.logger.error("Failed to encrypt auth failure email", {
                failure_type: "reset_password",
                reason,
                error: encryptError,
            });
            return null;
        }
    };

    const handleErrorMessageToShow = (errorCode: string) => {
        if (errorCode === "auth/user-not-found") {
            return "We couldn't find an account with that email. please check your email.";
        }

        return "Could not send password reset email, please try again later";
    };

    const onSubmit = async (data: IResetPasswordProps) => {
        try {
            await sendPasswordResetEmail(auth, data.email);

            toast({
                mode: "success",
                message:
                    "Password reset email sent successfully, please check your email",
            });
            const encryptedEmail = await getEncryptedEmailForReset(
                data.email,
                "Password reset email sent successfully"
            );
            if (encryptedEmail) {
                Sentry.logger.info("Password reset email sent successfully", {
                    encrypted_email: encryptedEmail,
                    failure_type: "reset_password",
                });
            }
            navigate("/login");
        } catch (error) {
            const encryptFailureReason =
                error instanceof FirebaseError
                    ? error.code
                    : "Error sending password reset email";
            const toastMessage =
                error instanceof FirebaseError
                    ? handleErrorMessageToShow(error.code)
                    : "Could not send password reset email, please try again later";
            toast({
                mode: "error",
                message: toastMessage,
            });
            const encryptedEmail = await getEncryptedEmailForReset(
                data.email,
                encryptFailureReason
            );
            Sentry.logger.error("Password reset email send failed", {
                encrypted_email: encryptedEmail,
                failure_type: "reset_password",
                reason: encryptFailureReason,
                toast_message: toastMessage,
            });
        }
    };

    return (
        <AuthWrapper
            footer={
                <>
                    Remember password?{" "}
                    <Link to="/login" className="text-primary font-medium">
                        Login
                    </Link>
                </>
            }
        >
            <>
                <h2 className="text-xxl font-bold leading-[32px] mb-4">
                    Request Password Reset
                </h2>
                <p className="text-[#525866] font-normal text-sm mb-40">
                    Enter your email address, and we’ll send a password reset
                    link. If you don’t see it, please check your spam folder.
                </p>
                <form className="mt-12" onSubmit={handleSubmit(onSubmit)}>
                    <div className="mb-24">
                        <Input
                            {...register("email")}
                            label="Email Address"
                            placeholder="Enter your email address"
                            hasError={!!errors.email}
                            errorText={errors.email?.message}
                        />
                    </div>
                    <Button
                        disabled={isSubmitting}
                        className=" w-full"
                        type="submit"
                        size="medium"
                    >
                        Send email
                    </Button>
                </form>
            </>
        </AuthWrapper>
    );
}
